import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {getCustomerName} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';
import {resolvePairedRecordCode} from './resolvePairedRecordCode';

export function useBusinessCasePairedRecord(businessCaseId: string | null): PairedRecordView {
  const {data: businessCase, isLoading} = useGetBusinessCaseQuery(
    isNotNil(businessCaseId) ? {businessCaseId} : skipToken
  );

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(businessCase?.customerId) ? {customerId: businessCase.customerId} : skipToken
  );

  const formatDateTime = useDateTimeFormatter();

  const activeOffer = businessCase?.offers?.[0];
  const isBrokerage =
    businessCase?.businessCaseInternalType === 'PURCHASE_BROKERAGE' ||
    isNotNil(businessCase?.brokerageBusinessCaseId);
  const saleVehicles = activeOffer?.saleVehicles ?? [];
  const purchaseVehicles = isBrokerage
    ? (activeOffer?.purchaseBrokerageVehicles ?? [])
    : (activeOffer?.purchaseVehicles ?? []);
  const vehicleIds = [
    ...saleVehicles.map((vehicle) => vehicle.vehicleId),
    ...purchaseVehicles.map((vehicle) => vehicle.vehicleId),
  ];

  const customerName = getCustomerName(customer);
  const createdAtFormatted = businessCase?.createdAt
    ? formatDateTime('dateShort', parseDate(businessCase.createdAt))
    : null;
  const updatedAtFormatted =
    businessCase?.updatedAt && isNotNil(parseDate(businessCase.updatedAt))
      ? formatDateTime('dateShort', parseDate(businessCase.updatedAt))
      : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(isNotNil(createdAtFormatted), `${i18n.t('general.labels.created')} ${createdAtFormatted}`)
    .when(
      isNotNil(updatedAtFormatted),
      `${i18n.t('general.labels.lastUpdated')} ${updatedAtFormatted}`
    );

  return {
    isLoading,
    code: resolvePairedRecordCode(businessCase?.code, isLoading),
    detailPath: isNotNil(businessCaseId)
      ? composePath(businessCaseRoutes.overview, {params: {id: businessCaseId}})
      : null,
    parameters,
    vehicleIds,
  };
}
